/**
 * MobX store for the troubleshooting page: stores list, API responses, and CSV upload state.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { rootContainer, TYPE } from '../../services/container/root';
import TroubleshootingService, { IStore } from '../../services/api/troubleshooting-service';
import { IFileData } from '../../components/file-upload/file-upload';

import { RMA_STATUS } from '../manage-returns/rma-page/rma-types';

export interface ITroubleshootingStore {
	stores: IStore[];
	shopifyResponse: any;
	fetchStores: () => void;
	fetchCustomer: (storeId: string | number, customerId: string) => void;
	fetchDiscountCode: (storeId: string | number, discountCode: string) => void;
	fetchOrder: (storeId: string | number, orderName: string) => void;
	fetchOrderById: (storeId: string | number, orderId: string) => void;
	fetchOrderByRMAId: (storeId: string | number, rmaId: string) => void;
	fetchDraftOrder: (storeId: string | number, orderName: string) => void;
	fetchDraftOrderById: (storeId: string | number, orderId: string) => void;
	fetchProduct: (storeId: string | number, productId: string) => void;
	fetchVariants: (storeId: string | number, variantIds: string) => void;
	fetchFulfillmentById: (storeId: string | number, fuylfillmentId: string | number) => void;
	fetchRMA: (storeId: string | number, rmaId: string | number) => void;
	fetchOrderEligibility: (storeId: string | number, orderName: string) => void;
	fetchOrderEligibilityLegacy: (storeId: string | number, orderName: string) => void;
	fetchSuperAdminActions: (storeId: string | number) => void;
	bulkUpdateRmaStatus: (storeId: string | number, rmaIDs: string, rmaStatus: number) => void;
	createSecondaryPortal: (storeId: string | number, portalName: string) => void;
	downloadTranslations: () => void;
	uploadTranslationsFile: (file: File, fileContent: string) => void;
	clearTranslationsCache: (storeId: string | number) => void;
	copyStoreSettings: (storeId: string | number, storeIdOrName: string | number) => void;
	transferRejectedRmaToCanceled: (storeId: string | number, rmaIds: string) => void;
	fetchStoreFeatures: (storeId: string | number) => void;
	activateFeatureForShop: (storeId: string | number, featureId: number) => void;
	deactivateFeatureForShop: (storeId: string | number, featureId: number) => void;
	setFeatureQuota: (storeId: string | number, featureId: number, quota: number) => void;
	changeStoreOwner: (storeId: string | number, newOwnerEmail: string) => void;
	fetchRefundCalculationResults: (storeId: string|number, rmaId: string|number) => void;
	fetchProfileExplanations: (storeId: string | number, capability: string) => Promise<void>;
	removeUserFromStore: (storeId: string|number, userEmail: string) => void;
	bulkAutoRefundRmas: (storeId: string|number, rmaIds: string, skipPolicyRuleCheck: boolean) => void;
	createPlatformStore: (data: {
		platform: string;
		credentials: Record<string, any>;
		email: string;
		firstname: string;
		surname: string;
	}) => void;
	validateEmail: (storeId: string | number) => void;
	uploadTroubleshootingCsv: (storeId: string | number, file: File) => Promise<void>;
}

class TroubleshootingStore implements ITroubleshootingStore {
	stores: IStore[] = [];
	shopifyResponse: any = {};

	private _isDownloading: boolean = false;
    private _isUploading: boolean = false;
    private _uploadedSuccessfuly: boolean = false;
	private _csvUploadInProgress: boolean = false;
	private get troubleshootingService(): TroubleshootingService {
		return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
	}

	constructor() {
		makeAutoObservable(this);
	}

	private setShopifyResponse(response: any) {
		runInAction(() => {
			this.shopifyResponse = response;
		});
	}

	public isDownloading(): boolean { return this._isDownloading};
    public isUploading(): boolean { return this._isUploading};
    public uploadedSuccessfuly(): boolean {return this._uploadedSuccessfuly }
	public get csvUploadInProgress(): boolean {
		return this._csvUploadInProgress;
	}
	public resetShopifyResponse() {
		runInAction(() => {
			this.shopifyResponse = {};
		});
	}

	async fetchStores() {
		const stores = await this.troubleshootingService.getStoresList();
		runInAction(() => {
			this.stores = stores;
		});
	}

	async fetchCustomer (storeId: string|number, customerId: string) {
		const shopifyResponse = await this.troubleshootingService.getShopifyCustomer(storeId, customerId);
		this.setShopifyResponse(shopifyResponse);
	}

	async fetchDiscountCode (storeId: string|number, discountCode: string) {
		const shopifyResponse = await this.troubleshootingService.getDiscountCode(storeId, discountCode);
		this.setShopifyResponse(shopifyResponse);
	}

	async fetchOrder (storeId: string|number, orderName: string) {
		const shopifyResponse = await this.troubleshootingService.getOrder(storeId, orderName);
		this.setShopifyResponse(shopifyResponse);
	}

	async fetchOrderById (storeId: string|number, orderId: string) {
		const shopifyResponse = await this.troubleshootingService.getOrderById(storeId, orderId);
		this.setShopifyResponse(shopifyResponse);
	}
	
	async fetchOrderByRMAId (storeId: string|number, rmaId: string) {
		const shopifyResponse = await this.troubleshootingService.getOrderByRMAId(storeId, rmaId);
		this.setShopifyResponse(shopifyResponse);
	}
	
	async fetchDraftOrder (storeId: string|number, orderName: string) {
		const shopifyResponse = await this.troubleshootingService.getDraftOrder(storeId, orderName);
		this.setShopifyResponse(shopifyResponse);
	}
	
	async fetchDraftOrderById (storeId: string|number, orderId: string) {
		const shopifyResponse = await this.troubleshootingService.getDraftOrderById(storeId, orderId);
		this.setShopifyResponse(shopifyResponse);
	}

	async fetchProduct (storeId: string|number, productId: string) {
		const shopifyResponse = await this.troubleshootingService.getProduct(storeId, productId);
		this.setShopifyResponse(shopifyResponse);
	}

    async fetchVariants (storeId: string|number, variantIds: string) {
        const shopifyResponse = await this.troubleshootingService.getVariants(storeId, variantIds);
        this.setShopifyResponse(shopifyResponse);
    }

	async fetchFulfillmentById (storeId: string|number, fuylfillmentId: string|number) {
		const shopifyResponse = await this.troubleshootingService.getFulfillmentById(storeId, fuylfillmentId);
		this.setShopifyResponse(shopifyResponse);
	}

	async fetchRMA (storeId: string|number, rmaId: string|number) {
		const response = await this.troubleshootingService.getRmaById(storeId, rmaId);
		this.setShopifyResponse(response.data?.[0]);
	}

	async fetchOrderEligibility (storeId: string|number, orderName: string) {
		// Fetch only order eligibility data - line item data is now included in the eligibility response
		const response = await this.troubleshootingService.getOrderEligibility(storeId, orderName);
		this.setShopifyResponse(response);
	}

	async fetchOrderEligibilityLegacy (storeId: string|number, orderName: string) {
		// Fetch legacy order eligibility data - returns raw JSON response
		const response = await this.troubleshootingService.getOrderEligibilityLegacy(storeId, orderName);
		this.setShopifyResponse(response);
	}

	async bulkUpdateRmaStatus (storeId: string|number, rmaIDs: string, status: RMA_STATUS) {
		const response = await this.troubleshootingService.bulkUpdateRmaStatus(storeId, rmaIDs, status);
		this.setShopifyResponse(response);
	}

    async bulkUpdateRmaStatusToDone (storeId: string|number, rmaIDs: string, skipPolicyRuleCheck: string) {
        const response = await this.troubleshootingService.bulkUpdateRmaStatus(storeId, rmaIDs, RMA_STATUS.DONE, skipPolicyRuleCheck);
        this.setShopifyResponse(response);
    }

	async createSecondaryPortal (storeId: string|number, portalName: string) {
		const response = await this.troubleshootingService.createSecondaryPortal(storeId, portalName);
		this.setShopifyResponse(response);
	}

	async fetchSuperAdminActions (storeId: string|number) {
		const response = await this.troubleshootingService.fetchSuperAdminActions(storeId);
		this.setShopifyResponse(response);
	}

	async downloadTranslations(): Promise<void> {
        try {
			runInAction(() => {
				this._isDownloading = true;
			});

            const path = await this.troubleshootingService.downloadTranslations();
			if (path && (path.startsWith('/') || path.startsWith('http'))) {
				window.open(path);
			} else {
				throw new Error('Invalid download path received');
			}

            runInAction(() => {
				this._isDownloading = false;
			});
        } catch {
            runInAction(() => {
				this._isDownloading = false;
			});
        }

    }

	async uploadTranslationsFile(file: File, fileContent: string): Promise<void> {
		try {
			if (!fileContent || fileContent.trim().length === 0) {
            	throw new Error('File content is empty or invalid');
        	}

			runInAction(() => {
           		this._uploadedSuccessfuly = false;
           		this._isUploading = true;
       		});

			const data: IFileData = {
				file_data: fileContent,
				mime_type: file.type,
				original_name: file.name,
				size: file.size,
			};
			const response = await this.troubleshootingService.uploadTranslationsFile(data);
		
			runInAction(() => {
				this._isUploading = false;
				this._uploadedSuccessfuly = true;
				this.shopifyResponse = response;
			});
		} catch {
			runInAction(() => {
				this._isUploading = false;
			});
		}
	}

	async clearTranslationsCache (storeId: string|number) {
		const response = await this.troubleshootingService.clearTranslationsCache(storeId);
		this.setShopifyResponse(response);
	}

	async copyStoreSettings (storeId: string|number, storeIdOrName: string|number) {
		const response = await this.troubleshootingService.copyStoreSettings(storeId, storeIdOrName);
		this.setShopifyResponse(response);
	}

	async transferRejectedRmaToCanceled (storeId: string|number, rmaIds: string) {
		const response = await this.troubleshootingService.transferRejectedRmaToCanceled(storeId, rmaIds);
		this.setShopifyResponse(response);
	}

	async fetchStoreFeatures (storeId: string|number) {
		const response = await this.troubleshootingService.getStoreFeatures(storeId);
		this.setShopifyResponse(response);
	}

	async activateFeatureForShop (storeId: string|number, featureId: number) {
		const response = await this.troubleshootingService.activateFeatureForShop(storeId, featureId);
		this.setShopifyResponse(response);
	}

	async deactivateFeatureForShop (storeId: string|number, featureId: number) {
		const response = await this.troubleshootingService.deactivateFeatureForShop(storeId, featureId);
		this.setShopifyResponse(response);
	}

	async setFeatureQuota (storeId: string|number, featureId: number, quota: number) {
		const response = await this.troubleshootingService.setFeatureQuota(storeId, featureId, quota);
		this.setShopifyResponse(response);
	}

	async changeStoreOwner (storeId: string|number, newOwnerEmail: string) {
		const response = await this.troubleshootingService.changeStoreOwner(storeId, newOwnerEmail);
		this.setShopifyResponse(response);
	}

	async fetchRefundCalculationResults (storeId: string|number, rmaId: string|number) {
		const response = await this.troubleshootingService.fetchRefundCalculationResults(storeId, rmaId);
		this.setShopifyResponse(response.data);
	}

	async fetchProfileExplanations (storeId: string | number, capability: string) {
		const response = await this.troubleshootingService.getProfileExplanations(storeId, capability);
		this.setShopifyResponse(response);
	}

	async removeUserFromStore (storeId: string|number, userEmail: string) {
		const response = await this.troubleshootingService.removeUserFromStore(storeId, userEmail);
		this.setShopifyResponse(response);
	}

	async bulkAutoRefundRmas (storeId: string|number, rmaIds: string, skipPolicyRuleCheck: boolean = false) {
		const response = await this.troubleshootingService.bulkAutoRefundRmas(storeId, rmaIds, skipPolicyRuleCheck);
		this.setShopifyResponse(response);
	}

	async createPlatformStore (data: {
		platform: string;
		credentials: Record<string, any>;
		email: string;
		firstname: string;
		surname: string;
	}) {
		const response = await this.troubleshootingService.createPlatformStore(data);
		// Store the response data, not the whole response object
		this.setShopifyResponse(response?.data || response);
		return response;
	}

	async validateEmail (storeId: string | number) {
		const response = await this.troubleshootingService.validateEmail(storeId);
		this.setShopifyResponse(response?.data ?? response);
	}

	async uploadTroubleshootingCsv (storeId: string | number, file: File): Promise<void> {
		try {
			runInAction(() => {
				this._csvUploadInProgress = true;
			});
			const response = await this.troubleshootingService.uploadTroubleshootingCsv(storeId, file);
			this.setShopifyResponse(response);
		} catch (error: any) {
			const message = error?.response?.data?.error ?? 'Invalid file type';
			runInAction(() => {
				this.shopifyResponse = { error: message };
			});
		} finally {
			runInAction(() => {
				this._csvUploadInProgress = false;
			});
		}
	}
}

/** TODO: find a way to instantiate store by demand */
export default new TroubleshootingStore();
